import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { RootState } from "../../../redux/store";
import { SupportItem, SupportPayload, SupportState } from "./types/support-types";

const API_BASE = process.env.REACT_APP_API_URL || "";

async function handleResp(res: Response) {
  const text = await res.text();
  let payload: any = null;

  try {
    payload = text ? JSON.parse(text) : null;
  } catch {
    payload = text;
  }

  if (!res.ok) {
    const msg =
      (payload && (payload.message || payload.error)) ||
      res.statusText ||
      "Request failed";
    throw new Error(msg);
  }

  return payload;
}

export const fetchSupport = createAsyncThunk<
  SupportItem[],
  void,
  { rejectValue: string; state: RootState }
>("support/fetch", async (_, { rejectWithValue, getState }) => {
  const token = (getState() as any).auth?.token;
  if (!token) return rejectWithValue("Unauthorized");

  try {
    const res = await fetch(`${API_BASE}/support`, {
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    });
    const data: any = await handleResp(res);
    // paginated: { success, data: { data: [...], current_page, ... } }
    if (data?.data?.data && Array.isArray(data.data.data)) return data.data.data;
    if (data?.data && Array.isArray(data.data)) return data.data;
    if (Array.isArray(data)) return data;
    return [];
  } catch (err: any) {
    return rejectWithValue(err?.message ?? "Failed to fetch support items");
  }
});

export const createSupport = createAsyncThunk<
  SupportItem,
  SupportPayload,
  { rejectValue: string; state: RootState }
>("support/create", async (payload, { rejectWithValue, getState }) => {
  const token = (getState() as any).auth?.token;
  if (!token) return rejectWithValue("Unauthorized");

  try {
    const res = await fetch(`${API_BASE}/support`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });
    const data: any = await handleResp(res);
    return data?.data ?? data;
  } catch (err: any) {
    return rejectWithValue(err?.message ?? "Failed to create support item");
  }
});

export const updateSupport = createAsyncThunk<
  SupportItem,
  { id: number } & Partial<SupportPayload>,
  { rejectValue: string; state: RootState }
>("support/update", async ({ id, ...payload }, { rejectWithValue, getState }) => {
  const token = (getState() as any).auth?.token;
  if (!token) return rejectWithValue("Unauthorized");

  try {
    const res = await fetch(`${API_BASE}/support/${id}`, {
      method: "PUT",
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });
    const data: any = await handleResp(res);
    return data?.data ?? data;
  } catch (err: any) {
    return rejectWithValue(err?.message ?? "Failed to update support item");
  }
});

export const deleteSupport = createAsyncThunk<
  number,
  number,
  { rejectValue: string; state: RootState }
>("support/delete", async (id, { rejectWithValue, getState }) => {
  const token = (getState() as any).auth?.token;
  if (!token) return rejectWithValue("Unauthorized");

  try {
    const res = await fetch(`${API_BASE}/support/${id}`, {
      method: "DELETE",
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    });
    await handleResp(res);
    return id;
  } catch (err: any) {
    return rejectWithValue(err?.message ?? "Failed to delete support item");
  }
});

const initialState: SupportState = {
  items: [],
  loading: false,
  saving: false,
  error: null,
};

const supportSlice = createSlice({
  name: "support",
  initialState,
  reducers: {
    clearSupportError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSupport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSupport.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchSupport.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || "Failed to fetch support items";
      })
      .addCase(createSupport.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(createSupport.fulfilled, (state, action) => {
        state.saving = false;
        state.items = [action.payload, ...state.items];
      })
      .addCase(createSupport.rejected, (state, action) => {
        state.saving = false;
        state.error = (action.payload as string) || "Failed to create support item";
      })
      .addCase(updateSupport.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(updateSupport.fulfilled, (state, action) => {
        state.saving = false;
        const idx = state.items.findIndex((item) => item.id === action.payload.id);
        if (idx >= 0) state.items[idx] = action.payload;
      })
      .addCase(updateSupport.rejected, (state, action) => {
        state.saving = false;
        state.error = (action.payload as string) || "Failed to update support item";
      })
      .addCase(deleteSupport.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(deleteSupport.fulfilled, (state, action) => {
        state.saving = false;
        state.items = state.items.filter((item) => item.id !== action.payload);
      })
      .addCase(deleteSupport.rejected, (state, action) => {
        state.saving = false;
        state.error = (action.payload as string) || "Failed to delete support item";
      });
  },
});

export const { clearSupportError } = supportSlice.actions;

export const selectSupport = (state: RootState) => (state as any).support?.items ?? [];
export const selectSupportLoading = (state: RootState) => (state as any).support?.loading ?? false;
export const selectSupportSaving = (state: RootState) => (state as any).support?.saving ?? false;
export const selectSupportError = (state: RootState) => (state as any).support?.error ?? null;

export default supportSlice.reducer;
